import { useEffect, useState, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { SERVICE_CATEGORIES } from '../../../config/ieumConfig';
import Pagination from '../../../components/common/Pagination';
import {
  DesktopListOnly, MobileCardField, MobileCardGrid, MobileCardHeader,
  MobileCardHeading, MobileCardLabel, MobileCardList, MobileCardStatus,
  MobileCardSubtitle, MobileCardTitle, MobileCardValue, MobileListCard,
  MobileListState,
} from '../../../components/common/AdminMobileList';
import { getAllRealtors } from '../../../services/memberService';

const PER_PAGE = 20;

const Wrap = styled.div``;

const TopRow = styled.div`
  display: flex; justify-content: space-between; align-items: flex-end;
  margin-bottom: 16px;
  gap: 12px; flex-wrap: wrap;
  @media (max-width: 768px) { align-items: stretch; margin-bottom: 14px; > button { width: 100%; } }
`;
const Title = styled.h1` font-size: 18px; font-weight: 600; `;
const SubText = styled.span`
  font-size: 13px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-left: 8px;
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 12px;
  margin-bottom: 20px;
  @media (max-width: 768px) { grid-template-columns: repeat(2, 1fr); }
`;
const StatCard = styled.div`
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  padding: 16px 18px;
`;
const StatLabel = styled.div`
  font-size: 12px; color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 6px;
`;
const StatValue = styled.div`
  font-size: 20px; font-weight: 600;
  color: ${({ $c }) => $c || '#1F2937'};
`;

const FilterBar = styled.div`
  display: flex; gap: 10px; margin-bottom: 16px; flex-wrap: wrap;
  @media (max-width: 768px) { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 8px; }
`;
const SearchInput = styled.input`
  padding: 8px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  width: 280px;
  @media (max-width: 768px) { grid-column: 1 / -1; width: 100%; min-height: 44px; }
`;
const Select = styled.select`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 8px;
  font-size: 13px;
  background: #fff;
  cursor: pointer;
  @media (max-width: 768px) { width: 100%; min-width: 0; min-height: 44px; }
`;

const Btn = styled.button`
  padding: 7px 13px;
  border-radius: 7px;
  font-size: 13px; font-weight: 500;
  border: 1px solid ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : theme.colors.border};
  background: ${({ theme, $variant }) =>
    $variant === 'primary' ? theme.colors.primary : '#fff'};
  color: ${({ $variant }) => ($variant === 'primary' ? '#fff' : '#374151')};
  cursor: pointer;
  &:hover { opacity: 0.92; }
  @media (max-width: 768px) { min-height: 44px; }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: #fff;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: 10px;
  overflow: hidden;

  thead tr { background: #F9FAFB; }
  th, td {
    padding: 12px 14px;
    text-align: left;
    font-size: 13px;
  }
  th {
    font-weight: 500;
    color: ${({ theme }) => theme.colors.textSecondary};
    font-size: 12px;
    white-space: nowrap;
  }
  th.sortable {
    cursor: pointer;
    user-select: none;
    &:hover { color: ${({ theme }) => theme.colors.primary}; }
  }
  th .sortInd {
    display: inline-block;
    margin-left: 6px;
    color: #C0C7D1;
    font-size: 10px;
    line-height: 1;
    vertical-align: middle;
  }
  th.active .sortInd { color: ${({ theme }) => theme.colors.primary}; }
  tbody tr {
    border-top: 1px solid ${({ theme }) => theme.colors.borderLight};
    &:hover { background: #F9FAFB; }
  }
`;

const ServiceTags = styled.div`
  display: flex; flex-wrap: wrap; gap: 4px;
`;
const ServiceTag = styled.span`
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 11px;
  color: #fff;
  background: ${({ $c }) => $c || '#9CA3AF'};
`;

const SourceText = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ $c }) => $c || '#6B7280'};
`;

const sourceLabel = (type, realtorName) => {
  // realtor 경로면 유입/추천 구분 없이 모두 "부동산" — 실적 인정은 동일
  if (realtorName || type === 'invite') return { label: '부동산', color: '#4A6CF7' };
  if (type === 'landing') return { label: '직접', color: '#22C55E' };
  return { label: '기타', color: '#9CA3AF' };
};

const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  if (typeof ts === 'string') return new Date(ts);
  return null;
};

const formatDate = (ts) => {
  const d = toDate(ts);
  if (!d) return '-';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const areaSizeLabel = (a) => ({
  under_12: '12평 이하', between_12_20: '12~20평', over_20: '20평 이상',
}[a] || a || '-');

// ───── 로드 함수 ─────
const loadCustomers = async () => {
  const [custSnap, reqSnap, realtors] = await Promise.all([
    getDocs(collection(db, 'customers')),
    getDocs(collection(db, 'service_requests')),
    getAllRealtors().catch(() => []),
  ]);

  const customers = custSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
  const requests = reqSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

  // realtor 이름 캐시 (1회 fetch)
  const realtorMap = new Map(
    realtors.map((r) => [r.uid, r.businessName || r.business_name || r.name || ''])
  );

  // customer_id 기준 service_requests 인덱스
  const reqsByCustomer = new Map();
  requests.forEach((r) => {
    const cid = r.customer_id || r.customerId;
    if (!cid) return;
    if (!reqsByCustomer.has(cid)) reqsByCustomer.set(cid, []);
    reqsByCustomer.get(cid).push(r);
  });

  return customers.map((c) => {
    const reqs = reqsByCustomer.get(c.id) || [];
    const services = Array.from(new Set(reqs.map((r) => r.category).filter(Boolean)));
    const realtorId = c.source_realtor_id || c.sourceRealtorId;
    return { ...c, services, realtorName: realtorId ? realtorMap.get(realtorId) || '' : '' };
  });
};

const CustomerList = () => {
  const [customers, setCustomers] = useState([]);
  const [search, setSearch] = useState('');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [sortKey, setSortKey] = useState('createdAt');
  const [sortDir, setSortDir] = useState('desc');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const list = await loadCustomers();
      setCustomers(list);
    } catch (err) {
      console.error(err);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // 통계
  const stats = useMemo(() => {
    const total = customers.length;
    const hasService = customers.filter((c) => c.services.length > 0).length;
    const hasMulti = customers.filter((c) => c.services.length >= 2).length;
    const thisMonth = customers.filter((c) => {
      const d = toDate(c.created_at || c.createdAt);
      if (!d) return false;
      const n = new Date();
      return d.getFullYear() === n.getFullYear() && d.getMonth() === n.getMonth();
    }).length;
    return { total, hasService, hasMulti, thisMonth };
  }, [customers]);

  const filtered = useMemo(() => {
    let list = customers.filter((c) => {
      if (serviceFilter === 'with_service' && c.services.length === 0) return false;
      if (serviceFilter === 'no_service' && c.services.length > 0) return false;
      if (serviceFilter !== 'all' && serviceFilter !== 'with_service' && serviceFilter !== 'no_service') {
        if (!c.services.includes(serviceFilter)) return false;
      }
      if (sourceFilter !== 'all') {
        const type = c.source_type || c.sourceType || 'direct';
        if (type !== sourceFilter) return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const name = (c.name || '').toLowerCase();
        const phone = (c.phone || '').toLowerCase();
        const addr = ((c.moving_address || c.address) || '').toLowerCase();
        if (!name.includes(q) && !phone.includes(q) && !addr.includes(q)) return false;
      }
      return true;
    });
    const getVal = (c) => {
      if (sortKey === 'name') return (c.name || '').toLowerCase();
      if (sortKey === 'movingDate') return c.moving_date || '';
      return toDate(c.created_at || c.createdAt)?.getTime() || 0;
    };
    list.sort((a, b) => {
      const va = getVal(a);
      const vb = getVal(b);
      if (va < vb) return sortDir === 'asc' ? -1 : 1;
      if (va > vb) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [customers, serviceFilter, sourceFilter, sortKey, sortDir, search]);

  const toggleSort = (key) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'createdAt' ? 'desc' : 'asc');
    }
    setPage(1);
  };

  const sortInd = (key) => {
    if (sortKey !== key) return <span className="sortInd">▲▼</span>;
    return <span className="sortInd">{sortDir === 'asc' ? '▲' : '▼'}</span>;
  };

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <Wrap>
      <TopRow>
        <div>
          <Title>일반회원(고객) 관리<SubText>리드 관리 · 통계</SubText></Title>
        </div>
        <Btn onClick={load}>{loading ? '불러오는 중...' : '새로고침'}</Btn>
      </TopRow>

      {/* 통계 */}
      <StatGrid>
        <StatCard>
          <StatLabel>전체 고객</StatLabel>
          <StatValue>{stats.total}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>서비스 신청 고객</StatLabel>
          <StatValue $c="#22C55E">{stats.hasService}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>복수 서비스 신청</StatLabel>
          <StatValue $c="#4A6CF7">{stats.hasMulti}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>이번달 신규</StatLabel>
          <StatValue $c="#F59E0B">{stats.thisMonth}</StatValue>
        </StatCard>
      </StatGrid>

      <FilterBar>
        <SearchInput
          placeholder="이름 / 전화번호 / 주소 검색"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
        />
        <Select value={serviceFilter} onChange={(e) => { setServiceFilter(e.target.value); setPage(1); }}>
          <option value="all">전체 서비스</option>
          <option value="with_service">서비스 있음</option>
          <option value="no_service">서비스 없음</option>
          {Object.entries(SERVICE_CATEGORIES).map(([k, v]) => (
            <option key={k} value={k}>{v.label}</option>
          ))}
        </Select>
        <Select value={sourceFilter} onChange={(e) => { setSourceFilter(e.target.value); setPage(1); }}>
          <option value="all">유입 전체</option>
          <option value="invite">부동산추천</option>
          <option value="landing">랜딩</option>
          <option value="direct">직접</option>
        </Select>
      </FilterBar>

      <MobileCardList aria-label="일반회원 카드 목록">
        {paged.map((c) => {
          const src = sourceLabel(c.source_type || c.sourceType || 'direct', c.realtorName);
          return (
            <MobileListCard key={c.id}>
              <MobileCardHeader>
                <MobileCardHeading>
                  <MobileCardTitle>{c.name || '-'}</MobileCardTitle>
                  <MobileCardSubtitle>{c.services.length > 0 ? `신청 서비스 ${c.services.length}건` : '신청 서비스 없음'}</MobileCardSubtitle>
                </MobileCardHeading>
                <MobileCardStatus><SourceText $c={src.color}>{src.label}</SourceText></MobileCardStatus>
              </MobileCardHeader>
              <MobileCardGrid>
                <MobileCardField><MobileCardLabel>연락처</MobileCardLabel><MobileCardValue>{c.phone || '-'}</MobileCardValue></MobileCardField>
                <MobileCardField><MobileCardLabel>이사일</MobileCardLabel><MobileCardValue>{c.moving_date || '-'}</MobileCardValue></MobileCardField>
                <MobileCardField><MobileCardLabel>평수</MobileCardLabel><MobileCardValue>{areaSizeLabel(c.area_size || c.areaSize)}</MobileCardValue></MobileCardField>
                <MobileCardField><MobileCardLabel>등록일</MobileCardLabel><MobileCardValue>{formatDate(c.created_at || c.createdAt)}</MobileCardValue></MobileCardField>
                <MobileCardField $wide><MobileCardLabel>주소</MobileCardLabel><MobileCardValue>{c.moving_address || c.address || '-'}</MobileCardValue></MobileCardField>
                <MobileCardField $wide>
                  <MobileCardLabel>서비스</MobileCardLabel>
                  <MobileCardValue>
                    {c.services.length === 0 ? '-' : (
                      <ServiceTags>
                        {c.services.map((service) => <ServiceTag key={service} $c={SERVICE_CATEGORIES[service]?.color}>{SERVICE_CATEGORIES[service]?.label || service}</ServiceTag>)}
                      </ServiceTags>
                    )}
                  </MobileCardValue>
                </MobileCardField>
                {c.realtorName && <MobileCardField $wide><MobileCardLabel>추천 공인중개사</MobileCardLabel><MobileCardValue>{c.realtorName}</MobileCardValue></MobileCardField>}
              </MobileCardGrid>
            </MobileListCard>
          );
        })}
        {loading ? <MobileListState>불러오는 중...</MobileListState> : paged.length === 0 && <MobileListState>검색 결과가 없습니다.</MobileListState>}
      </MobileCardList>

      <DesktopListOnly>
      <Table>
        <thead>
          <tr>
            <th
              className={`sortable${sortKey === 'name' ? ' active' : ''}`}
              onClick={() => toggleSort('name')}
            >고객명{sortInd('name')}</th>
            <th>연락처</th>
            <th>주소</th>
            <th>평수</th>
            <th
              className={`sortable${sortKey === 'movingDate' ? ' active' : ''}`}
              onClick={() => toggleSort('movingDate')}
            >이사일{sortInd('movingDate')}</th>
            <th>서비스</th>
            <th>유입</th>
            <th
              className={`sortable${sortKey === 'createdAt' ? ' active' : ''}`}
              onClick={() => toggleSort('createdAt')}
            >등록일{sortInd('createdAt')}</th>
          </tr>
        </thead>
        <tbody>
          {paged.map((c) => {
            const src = sourceLabel(c.source_type || c.sourceType || 'direct', c.realtorName);
            return (
              <tr key={c.id}>
                <td style={{ fontWeight: 500 }}>{c.name || '-'}</td>
                <td>{c.phone || '-'}</td>
                <td style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {c.moving_address || c.address || '-'}
                </td>
                <td>{areaSizeLabel(c.area_size || c.areaSize)}</td>
                <td>{c.moving_date || '-'}</td>
                <td>
                  {c.services.length === 0 ? '-' : (
                    <ServiceTags>
                      {c.services.map((s) => (
                        <ServiceTag key={s} $c={SERVICE_CATEGORIES[s]?.color}>{SERVICE_CATEGORIES[s]?.label || s}</ServiceTag>
                      ))}
                    </ServiceTags>
                  )}
                </td>
                <td>
                  <SourceText $c={src.color}>{src.label}</SourceText>
                  {c.realtorName && <div style={{ fontSize: 11, color: '#9CA3AF' }}>{c.realtorName}</div>}
                </td>
                <td style={{ color: '#6B7280' }}>{formatDate(c.created_at || c.createdAt)}</td>
              </tr>
            );
          })}
          {paged.length === 0 && (
            <tr><td colSpan={8} style={{ textAlign: 'center', color: '#9CA3AF', padding: 32 }}>검색 결과가 없습니다.</td></tr>
          )}
        </tbody>
      </Table>
      </DesktopListOnly>

      <Pagination current={page} total={Math.max(1, Math.ceil(filtered.length / PER_PAGE))} onChange={setPage} />
    </Wrap>
  );
};

export default CustomerList;
